import AppErrorFallback from '@/common/components/error-state/AppErrorFallback.tsx';
import LightListFeaturePage from '@/common/components/list-route/LightListFeaturePage.tsx';
import {
  getBannerControllerGetBannerQueryKey,
  useBannerControllerGetBanner,
  useBannerControllerUpdateBanner,
} from '@/generated/api/banner.ts';
import {
  BannerDtoSeverity,
  type BannerDto,
} from '@/generated/api/pacApi.schemas.ts';
import { getQueryErrorEventId } from '@/main.tsx';
import {
  Button,
  Group,
  Paper,
  Select,
  Stack,
  Switch,
  Textarea,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

export const Route = createFileRoute('/admin/general/banner')({
  component: RouteComponent,
});

const severityOptions = Object.values(BannerDtoSeverity).map((severity) => ({
  value: severity,
  label: severity,
}));

function RouteComponent() {
  const queryClient = useQueryClient();
  const bannerQuery = useBannerControllerGetBanner();
  const updateBannerMutation = useBannerControllerUpdateBanner({
    mutation: {
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: getBannerControllerGetBannerQueryKey(),
        });
        notifications.show({
          color: 'green',
          title: 'Banner settings saved',
          message: 'The banner settings were updated.',
        });
      },
      onError: () => {
        notifications.show({
          color: 'red',
          title: 'Failed to save banner settings',
          message: 'Review the banner settings and try again.',
        });
      },
    },
  });
  const [formValues, setFormValues] = useState<BannerDto>({
    active: false,
    severity: BannerDtoSeverity.INFO,
    message: '',
  });

  useEffect(() => {
    if (bannerQuery.data) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setFormValues(bannerQuery.data);
    }
  }, [bannerQuery.data]);

  if (bannerQuery.isError) {
    return (
      <LightListFeaturePage title="Banner">
        <AppErrorFallback
          title="Failed to load banner settings"
          message="We couldn't load banner settings right now. Try again or report this issue."
          eventId={getQueryErrorEventId(['banner'])}
          onRetry={() => bannerQuery.refetch()}
          onReload={() => window.location.reload()}
          onBack={() => window.history.back()}
        />
      </LightListFeaturePage>
    );
  }

  const disabled = bannerQuery.isLoading || updateBannerMutation.isPending;

  return (
    <LightListFeaturePage
      title="Banner"
      subtitle="Manage the global alert banner shown across the app."
    >
      <Paper withBorder radius={6} p="md" maw={640}>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            updateBannerMutation.mutate({ data: formValues });
          }}
        >
          <Stack gap="md">
            <Switch
              label="Active"
              checked={formValues.active}
              disabled={disabled}
              onChange={(event) =>
                setFormValues((current) => ({
                  ...current,
                  active: event.currentTarget.checked,
                }))
              }
            />
            <Select
              label="Severity"
              data={severityOptions}
              value={formValues.severity}
              allowDeselect={false}
              disabled={disabled}
              onChange={(value) => {
                if (!value) return;
                setFormValues((current) => ({
                  ...current,
                  severity: value as BannerDto['severity'],
                }));
              }}
            />
            <Textarea
              label="Message"
              minRows={4}
              value={formValues.message}
              disabled={disabled}
              onChange={(event) =>
                setFormValues((current) => ({
                  ...current,
                  message: event.currentTarget.value,
                }))
              }
            />
            <Group justify="flex-end">
              <Button
                type="submit"
                loading={updateBannerMutation.isPending}
                disabled={disabled}
              >
                Save
              </Button>
            </Group>
          </Stack>
        </form>
      </Paper>
    </LightListFeaturePage>
  );
}
